import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import Skeleton from '../../components/Skeleton';

function HandoffProtocolContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();

  const [handoffs, setHandoffs] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [artists, setArtists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [loading, setLoading] = useState(true);
  const [buddyLoading, setBuddyLoading] = useState(true);
  const [showAddBuddy, setShowAddBuddy] = useState(false);
  const [newBuddy, setNewBuddy] = useState({ artist_id: '', buddy_id: '' });

  const fetchHandoffs = async () => {
    setLoading(true);
    try {
      const data = await api.getManagerPremiumBookings('handoff_in_progress');
      const filtered = selectedBranchId
        ? (data || []).filter(h => String(h.branch_id) === String(selectedBranchId))
        : data || [];
      setHandoffs(filtered);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchBuddies = async () => {
    setBuddyLoading(true);
    try {
      const data = await api.getManagerArtistBuddies(selectedBranchId || undefined);
      setBuddies(data || []);
    } catch (err) { console.error(err); }
    finally { setBuddyLoading(false); }
  };

  useEffect(() => {
    api.getManagerBranches().then(setBranches).catch(console.error);
    api.getManagerArtists().then(setArtists).catch(console.error);
  }, []);

  useEffect(() => { fetchHandoffs(); fetchBuddies(); }, [selectedBranchId]);

  const completeHandoff = async (bookingId) => {
    try {
      await api.updateManagerPremiumBookingStatus(bookingId, 'completed');
      showAlert(t('vip.handoffCompleted', 'Handoff completed'));
      fetchHandoffs();
    } catch (err) { showAlert(err.message); }
  };

  const addBuddy = async () => {
    if (!newBuddy.artist_id || !newBuddy.buddy_id) {
      showAlert(t('vip.selectBothArtists', 'Please select both artists'));
      return;
    }
    if (newBuddy.artist_id === newBuddy.buddy_id) {
      showAlert(t('vip.cannotBuddySelf', 'An artist cannot be their own buddy'));
      return;
    }
    try {
      await api.createManagerArtistBuddy({
        artist_id: newBuddy.artist_id,
        buddy_id: newBuddy.buddy_id,
        branch_id: selectedBranchId || undefined,
      });
      showAlert(t('vip.buddyAdded', 'Buddy pair added'));
      setShowAddBuddy(false);
      setNewBuddy({ artist_id: '', buddy_id: '' });
      fetchBuddies();
    } catch (err) { showAlert(err.message); }
  };

  const removeBuddy = async (buddyId) => {
    const confirmed = await showConfirm(t('vip.confirmDeleteBuddy', 'Remove this buddy pair?'));
    if (!confirmed) return;
    try {
      await api.deleteManagerArtistBuddy(buddyId);
      fetchBuddies();
    } catch (err) { showAlert(err.message); }
  };

  const avatarStyle = (color) => ({
    width: '40px', height: '40px', borderRadius: '50%',
    background: `linear-gradient(135deg, ${color}, ${color}88)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0,
  });

  return (
    <AdminLayout role="manager">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('vip.handoffProtocol', 'Handoff Protocol')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange }} />
      </div>

      {/* Branch Filter */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select
          value={selectedBranchId}
          onChange={e => setSelectedBranchId(e.target.value)}
          style={{ ...S.input, maxWidth: '300px' }}
        >
          <option value="">{t('vip.allBranches', 'All Branches')}</option>
          {branches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {/* Two Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', alignItems: 'start' }}>

        {/* LEFT: Active Handoffs */}
        <div>
          <h2 style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700,
            color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem',
          }}>
            {t('vip.activeHandoffs', 'Active Handoffs')}
          </h2>

          {loading ? <Skeleton count={3} height="160px" /> : handoffs.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: T.muted }}>{t('vip.noActiveHandoffs', 'No active handoffs')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {handoffs.map(h => (
                <div key={h.id} style={{
                  ...S.card,
                  padding: '1.25rem',
                  borderLeft: '4px solid #a855f7',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}>
                  {/* VIP Client Info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={avatarStyle('#f59e0b')}>
                      {(h.client_name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: T.white, fontSize: '0.9rem' }}>{h.client_name}</div>
                      <div style={{ fontSize: '0.72rem', color: T.muted }}>{h.service_name} &middot; {h.branch_name}</div>
                    </div>
                    <span style={{
                      marginLeft: 'auto', padding: '0.2rem 0.65rem', borderRadius: '20px',
                      fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase',
                      background: '#a855f718', color: '#a855f7',
                    }}>
                      {t('vip.handoff', 'Handoff')}
                    </span>
                  </div>

                  {/* Transfer Visualization */}
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '1rem', padding: '0.85rem', borderRadius: '10px',
                    background: T.bg,
                  }}>
                    {/* Artist A */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ ...avatarStyle('#3b82f6'), margin: '0 auto 0.35rem' }}>
                        {(h.artist_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: T.white, fontWeight: 600 }}>{h.artist_name}</div>
                    </div>

                    {/* Arrow */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      <div style={{ width: '40px', height: '2px', background: '#a855f7' }} />
                      <div style={{ color: '#a855f7', fontSize: '1.2rem', lineHeight: 1 }}>&#x279C;</div>
                      <div style={{ width: '40px', height: '2px', background: '#a855f7' }} />
                    </div>

                    {/* Artist B (Buddy) */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ ...avatarStyle('#22c55e'), margin: '0 auto 0.35rem' }}>
                        {(h.buddy_name || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: T.white, fontWeight: 600 }}>{h.buddy_name || t('vip.unassigned', 'Unassigned')}</div>
                    </div>
                  </div>

                  {/* Complete Button */}
                  <button
                    onClick={() => completeHandoff(h.id)}
                    style={{
                      padding: '0.6rem 1.2rem', borderRadius: '8px', border: 'none',
                      background: '#22c55e', color: '#fff', cursor: 'pointer',
                      fontWeight: 700, fontSize: '0.82rem', textTransform: 'uppercase',
                      letterSpacing: '0.04em', width: '100%', transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={e => { e.target.style.opacity = '0.85'; }}
                    onMouseLeave={e => { e.target.style.opacity = '1'; }}
                  >
                    {t('vip.confirmHandoffComplete', 'Confirm Handoff Complete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT: Buddy Artist Config */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem', fontWeight: 700,
              color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {t('vip.buddyConfig', 'Buddy Artist Config')}
            </h2>
            <button
              onClick={() => setShowAddBuddy(!showAddBuddy)}
              style={{
                padding: '0.4rem 1rem', borderRadius: '8px', border: 'none',
                background: T.orange, color: '#000', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.78rem',
              }}
            >
              {showAddBuddy ? t('common.cancel', 'Cancel') : t('vip.addBuddy', '+ Add Buddy')}
            </button>
          </div>

          {/* Add Buddy Form */}
          {showAddBuddy && (
            <div style={{ ...S.card, padding: '1.25rem', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <select
                value={newBuddy.artist_id}
                onChange={e => setNewBuddy(p => ({ ...p, artist_id: e.target.value }))}
                style={S.input}
              >
                <option value="">{t('vip.selectArtist', 'Select Artist')}</option>
                {artists.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <select
                value={newBuddy.buddy_id}
                onChange={e => setNewBuddy(p => ({ ...p, buddy_id: e.target.value }))}
                style={S.input}
              >
                <option value="">{t('vip.selectBuddy', 'Select Buddy Artist')}</option>
                {artists.filter(a => String(a.id) !== String(newBuddy.artist_id)).map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <button onClick={addBuddy} style={{
                padding: '0.5rem 1.2rem', borderRadius: '8px', border: 'none',
                background: '#22c55e', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.82rem',
              }}>
                {t('vip.saveBuddy', 'Save Buddy Pair')}
              </button>
            </div>
          )}

          {/* Buddy List */}
          {buddyLoading ? <Skeleton count={4} height="64px" /> : buddies.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center' }}>
              <p style={{ color: T.muted }}>{t('vip.noBuddies', 'No buddy pairs configured')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {buddies.map(b => (
                <div key={b.id} style={{
                  ...S.card,
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.85rem',
                }}>
                  {/* Artist */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <div style={avatarStyle('#3b82f6')}>
                      {(b.artist_name || '?')[0].toUpperCase()}
                    </div>
                    <span style={{ color: T.white, fontWeight: 600, fontSize: '0.88rem' }}>{b.artist_name}</span>
                  </div>

                  {/* Arrow */}
                  <div style={{ color: T.muted, fontSize: '1.1rem' }}>&#x279C;</div>

                  {/* Buddy */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <div style={avatarStyle('#22c55e')}>
                      {(b.buddy_name || '?')[0].toUpperCase()}
                    </div>
                    <span style={{ color: T.white, fontWeight: 600, fontSize: '0.88rem' }}>{b.buddy_name}</span>
                  </div>

                  {/* Delete */}
                  <button
                    onClick={() => removeBuddy(b.id)}
                    style={{
                      padding: '0.35rem 0.65rem', borderRadius: '6px',
                      border: 'none', background: '#ef444422', color: '#ef4444',
                      cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem',
                    }}
                    onMouseEnter={e => { e.target.style.background = '#ef444444'; }}
                    onMouseLeave={e => { e.target.style.background = '#ef444422'; }}
                  >
                    {t('common.delete', 'Delete')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default function HandoffProtocol() {
  return <ProtectedRoute requireSalonManager><HandoffProtocolContent /></ProtectedRoute>;
}
